import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useData } from "../../context/DataContext";
import {
  deleteEvaluationRound,
  getEvaluationRounds,
} from "../../api/evaluations";
import { exportExcel } from "../reports/reportExport";
import { isHrFamilyRole } from "../../utils/hrStaffHelpers";
import EvaluationComparisonChart from "../common/EvaluationComparisonChart";
import "./EvaluationResultsBoard.css";

function getApiErrorMessage(err, fallback) {
  const message = err?.response?.data?.message;
  if (Array.isArray(message)) return message.join("\n");
  if (typeof message === "string" && message.trim()) return message;
  return err?.message || fallback;
}

export function statusLabel(status) {
  if (status === "DRAFT") return "مسودة";
  if (status === "ACTIVE") return "مفتوحة";
  if (status === "CLOSED") return "مغلقة";
  return status || "—";
}

function raterTypeLabel(type) {
  if (type === "MANAGER") return "مدير مباشر";
  if (type === "PEER") return "زميل";
  if (type === "SUBORDINATE") return "مرؤوس";
  if (type === "SELF") return "تقييم ذاتي";
  if (type === "CUSTOM") return "مخصص";
  return type || "—";
}

export function currentMonthKey(ref = new Date()) {
  const year = ref.getFullYear();
  const month = String(ref.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
}

export function formatMonthLabel(monthKey) {
  const match = /^(\d{4})-(\d{2})$/.exec(monthKey || "");
  if (!match) return monthKey || "";
  const date = new Date(Number(match[1]), Number(match[2]) - 1, 1);
  return date.toLocaleDateString("ar-EG", { month: "long", year: "numeric" });
}

function dateKey(value) {
  const match = /^(\d{4}-\d{2}-\d{2})/.exec(String(value || ""));
  return match ? match[1] : "";
}

function monthEndKey(monthKey) {
  const match = /^(\d{4})-(\d{2})$/.exec(monthKey || "");
  if (!match) return "";
  const year = Number(match[1]);
  const month = Number(match[2]);
  const lastDay = new Date(year, month, 0).getDate();
  return `${monthKey}-${String(lastDay).padStart(2, "0")}`;
}

export function roundOverlapsMonth(round, monthKey) {
  if (!/^\d{4}-\d{2}$/.test(monthKey || "")) return false;
  const start = dateKey(round?.startDate);
  const end = dateKey(round?.endDate) || start;
  if (start) {
    return start <= monthEndKey(monthKey) && end >= `${monthKey}-01`;
  }
  return dateKey(round?.createdAt).startsWith(monthKey);
}

export function buildMissingSubmitters(rounds, monthKey, employeeIndex = new Map()) {
  const byEmployee = new Map();
  const seenRaterRows = new Set();
  (rounds || []).forEach((round) => {
    if (round?.status !== "ACTIVE" && round?.status !== "CLOSED") return;
    if (!roundOverlapsMonth(round, monthKey)) return;
    (round.subjects || []).forEach((subject) => {
      const subjectName =
        subject.employee?.fullName ||
        employeeIndex.get(subject.employeeId)?.name ||
        "موظف";
      (subject.raters || []).forEach((rater) => {
        if (rater.status === "SUBMITTED" || !rater.id || seenRaterRows.has(rater.id)) {
          return;
        }
        const employeeId = rater.raterId || rater.rater?.id;
        if (!employeeId) return;
        seenRaterRows.add(rater.id);
        const local = employeeIndex.get(employeeId);
        const existing = byEmployee.get(employeeId) || {
          id: employeeId,
          name: rater.rater?.fullName || local?.name || "موظف",
          code: rater.rater?.employeeCode || local?.code || "",
          jobTitle: rater.rater?.jobTitle || local?.jobTitle || "",
          branchName: rater.rater?.branch?.name || local?.branchName || "",
          pending: [],
        };
        existing.pending.push({
          id: rater.id,
          roundTitle: round.title || "",
          subjectName,
          raterType: rater.raterType,
          raterTypeLabel: raterTypeLabel(rater.raterType),
        });
        byEmployee.set(employeeId, existing);
      });
    });
  });
  return [...byEmployee.values()].sort((a, b) =>
    a.name.localeCompare(b.name, "ar"),
  );
}

export function buildMissingExcelRows(rows, monthLabel) {
  return (rows || []).map((row) => ({
    الشهر: monthLabel,
    "اسم الموظف": row.name,
    الكود: row.code || "",
    الوظيفة: row.jobTitle || "",
    الفرع: row.branchName || "",
    "عدد ما لم يُرسل": row.pending.length,
    "التقييمات غير المرسلة": row.pending
      .map(
        (item) =>
          `${item.roundTitle}: ${item.subjectName} (${item.raterTypeLabel})`,
      )
      .join(" | "),
  }));
}

function scoreTone(percent) {
  if (percent == null || Number.isNaN(Number(percent))) return "muted";
  const value = Number(percent);
  if (value >= 85) return "high";
  if (value >= 70) return "mid";
  return "low";
}

export function creatorLabel(round) {
  return (
    round?.createdBy?.employee?.fullName ||
    round?.createdBy?.username ||
    "—"
  );
}

export function creatorKey(round) {
  return round?.createdBy?.id || round?.createdById || "";
}

function canDeleteClosedRounds(role) {
  return isHrFamilyRole(role) || role === "gm" || role === "admin";
}

export function mapSubjectRow(subject, employeeIndex) {
  const local = employeeIndex.get(subject.employeeId);
  const submittedCount = (subject.raters || []).filter(
    (r) => r.status === "SUBMITTED",
  ).length;
  const totalRaters = subject.raters?.length || 0;
  const finalPercent =
    subject.finalPercent != null ? Number(subject.finalPercent) : null;
  return {
    subject,
    name: subject.employee?.fullName || local?.name || "موظف",
    code: subject.employee?.employeeCode || local?.code || "",
    jobTitle: subject.employee?.jobTitle || local?.jobTitle || "",
    branchName:
      subject.employee?.branch?.name || local?.branchName || "",
    submittedCount,
    totalRaters,
    finalPercent,
  };
}

function sortRows(rows, sortBy) {
  const sorted = [...rows];
  sorted.sort((a, b) => {
    if (sortBy === "name") {
      return a.name.localeCompare(b.name, "ar");
    }
    if (sortBy === "score-asc") {
      return (a.finalPercent ?? -1) - (b.finalPercent ?? -1);
    }
    if (a.finalPercent == null && b.finalPercent == null) return 0;
    if (a.finalPercent == null) return 1;
    if (b.finalPercent == null) return -1;
    return b.finalPercent - a.finalPercent;
  });
  return sorted;
}

export function buildExcelRows(sections) {
  return sections.flatMap((section) =>
    section.rows.map((row) => ({
      الجولة: section.round.title || "",
      الحالة: statusLabel(section.round.status),
      "مسؤول الموارد البشرية": creatorLabel(section.round),
      "اسم الموظف": row.name,
      الوظيفة: row.jobTitle || "",
      الكود: row.code || "",
      الفرع: row.branchName || "",
      التقييم: row.finalPercent != null ? row.finalPercent : "",
    })),
  );
}

export default function EvaluationResultsBoard() {
  const { role } = useAuth();
  const { employeesByBranch } = useData();
  const [rounds, setRounds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [hrFilter, setHrFilter] = useState("ALL");
  const [roundFilter, setRoundFilter] = useState("ALL");
  const [sortBy, setSortBy] = useState("score-desc");
  const [expandedId, setExpandedId] = useState(null);
  const [deletingId, setDeletingId] = useState("");
  const [missingMonth, setMissingMonth] = useState(() => currentMonthKey());

  const canDelete = canDeleteClosedRounds(role);

  const employeeIndex = useMemo(() => {
    const map = new Map();
    Object.values(employeesByBranch || {}).forEach((list) => {
      (list || []).forEach((emp) => {
        if (emp?.id) map.set(emp.id, emp);
      });
    });
    return map;
  }, [employeesByBranch]);

  const loadRounds = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const rows = await getEvaluationRounds();
      const list = Array.isArray(rows) ? rows : [];
      setRounds(
        list.filter(
          (round) => round.status === "ACTIVE" || round.status === "CLOSED",
        ),
      );
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر تحميل نتائج التقييمات"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRounds();
  }, [loadRounds]);

  const hrOptions = useMemo(() => {
    const map = new Map();
    rounds.forEach((round) => {
      const key = creatorKey(round);
      if (!key || map.has(key)) return;
      map.set(key, creatorLabel(round));
    });
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1], "ar"));
  }, [rounds]);

  const roundsAfterHr = useMemo(() => {
    if (hrFilter === "ALL") return rounds;
    return rounds.filter((round) => creatorKey(round) === hrFilter);
  }, [rounds, hrFilter]);

  const roundsAfterStatus = useMemo(() => {
    if (statusFilter === "ALL") return roundsAfterHr;
    return roundsAfterHr.filter((round) => round.status === statusFilter);
  }, [roundsAfterHr, statusFilter]);

  const roundOptions = roundsAfterStatus;

  useEffect(() => {
    if (roundFilter === "ALL") return;
    if (!roundOptions.some((round) => round.id === roundFilter)) {
      setRoundFilter("ALL");
    }
  }, [roundOptions, roundFilter]);

  const visibleRounds = useMemo(() => {
    if (roundFilter === "ALL") return roundOptions;
    return roundOptions.filter((round) => round.id === roundFilter);
  }, [roundOptions, roundFilter]);

  const sections = useMemo(() => {
    const query = search.trim().toLowerCase();
    const built = visibleRounds.map((round) => {
      const mapped = (round.subjects || []).map((subject) =>
        mapSubjectRow(subject, employeeIndex),
      );
      const filtered = query
        ? mapped.filter((row) => {
            const hay = `${row.name} ${row.code} ${row.jobTitle} ${row.branchName}`
              .toLowerCase()
              .trim();
            return hay.includes(query);
          })
        : mapped;
      return { round, rows: sortRows(filtered, sortBy) };
    });
    if (query) return built.filter((section) => section.rows.length > 0);
    return built;
  }, [visibleRounds, employeeIndex, search, sortBy]);

  const stats = useMemo(() => {
    const rows = sections.flatMap((section) => section.rows);
    const withScore = rows.filter((row) => row.finalPercent != null);
    const avg =
      withScore.length > 0
        ? Math.round(
            (withScore.reduce((sum, row) => sum + row.finalPercent, 0) /
              withScore.length) *
              10,
          ) / 10
        : null;
    const completed = rows.filter(
      (row) => row.totalRaters > 0 && row.submittedCount === row.totalRaters,
    ).length;
    return {
      rounds: sections.length,
      total: rows.length,
      scored: withScore.length,
      avg,
      completed,
    };
  }, [sections]);

  const missingMonthLabel = formatMonthLabel(missingMonth);
  const missingSubmitters = useMemo(
    () => buildMissingSubmitters(rounds, missingMonth, employeeIndex),
    [rounds, missingMonth, employeeIndex],
  );
  const monthHasRounds = useMemo(
    () =>
      rounds.some(
        (round) =>
          (round.status === "ACTIVE" || round.status === "CLOSED") &&
          roundOverlapsMonth(round, missingMonth),
      ),
    [rounds, missingMonth],
  );

  const handleExport = () => {
    const excelRows = buildExcelRows(sections);
    if (!excelRows.length) {
      setError("لا توجد نتائج لتصديرها");
      return;
    }
    setError("");
    exportExcel(excelRows, "نتائج-التقييمات", "نتائج التقييمات");
  };

  const handleExportMissing = () => {
    const excelRows = buildMissingExcelRows(missingSubmitters, missingMonthLabel);
    if (!excelRows.length) {
      setError("لا يوجد موظفون لم يرسلوا التقييم في هذا الشهر");
      return;
    }
    setError("");
    exportExcel(
      excelRows,
      `من-لم-يرسلوا-التقييم-${missingMonth}`,
      "من لم يرسلوا التقييم",
    );
  };

  const handleDelete = async (round) => {
    const confirmed = window.confirm(
      `حذف التقييم «${round.title}»؟ سيتم حذف النتائج والإجابات ولا يمكن التراجع.`,
    );
    if (!confirmed) return;
    setDeletingId(round.id);
    setError("");
    setSuccess("");
    try {
      await deleteEvaluationRound(round.id);
      setRounds((prev) => prev.filter((item) => item.id !== round.id));
      setSuccess("تم حذف التقييم المغلق");
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر حذف التقييم"));
    } finally {
      setDeletingId("");
    }
  };

  return (
    <div className="eval-results" dir="rtl">
      <div className="eval-results__header">
        <div>
          <h1 className="ess-home-title">نتائج التقييمات</h1>
          <p className="ess-home-subtitle">
            التقييمات المفتوحة والمغلقة تظهر تحت بعضها
          </p>
        </div>
        <button
          type="button"
          className="btn btn-success"
          onClick={handleExport}
          disabled={loading || stats.total === 0}
        >
          تصدير إكسل
        </button>
      </div>

      {error ? <div className="eval-results__alert">{error}</div> : null}
      {success ? <div className="eval-results__success">{success}</div> : null}

      <div className="eval-results__toolbar">
        <div className="eval-results__field">
          <label htmlFor="eval-status">حالة الجولة</label>
          <select
            id="eval-status"
            className="form-control"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="ALL">المفتوحة والمغلقة</option>
            <option value="ACTIVE">المفتوحة</option>
            <option value="CLOSED">المغلقة</option>
          </select>
        </div>

        <div className="eval-results__field">
          <label htmlFor="eval-hr">مسؤول الموارد البشرية</label>
          <select
            id="eval-hr"
            className="form-control"
            value={hrFilter}
            onChange={(e) => setHrFilter(e.target.value)}
          >
            <option value="ALL">كل المسؤولين</option>
            {hrOptions.map(([id, name]) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </select>
        </div>

        <div className="eval-results__field">
          <label htmlFor="eval-round">الجولة</label>
          <select
            id="eval-round"
            className="form-control"
            value={roundFilter}
            onChange={(e) => setRoundFilter(e.target.value)}
            disabled={loading || roundOptions.length === 0}
          >
            <option value="ALL">كل الجولات</option>
            {roundOptions.map((round) => (
              <option key={round.id} value={round.id}>
                {round.title} — {statusLabel(round.status)}
              </option>
            ))}
          </select>
        </div>

        <div className="eval-results__field eval-results__field--grow">
          <label htmlFor="eval-search">بحث عن موظف</label>
          <input
            id="eval-search"
            className="form-control"
            placeholder="الاسم، الكود، المسمى، الفرع..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="eval-results__field">
          <label htmlFor="eval-sort">ترتيب</label>
          <select
            id="eval-sort"
            className="form-control"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="score-desc">الأعلى نتيجة</option>
            <option value="score-asc">الأقل نتيجة</option>
            <option value="name">الاسم</option>
          </select>
        </div>
      </div>

      <div className="eval-results__stats">
        <div className="eval-results__stat">
          <span className="eval-results__stat-label">الجولات</span>
          <strong>{stats.rounds}</strong>
        </div>
        <div className="eval-results__stat">
          <span className="eval-results__stat-label">الموظفون</span>
          <strong>{stats.total}</strong>
        </div>
        <div className="eval-results__stat">
          <span className="eval-results__stat-label">لهم نتيجة</span>
          <strong>{stats.scored}</strong>
        </div>
        <div className="eval-results__stat">
          <span className="eval-results__stat-label">متوسط النتيجة</span>
          <strong>{stats.avg != null ? `${stats.avg}%` : "—"}</strong>
        </div>
        <div className="eval-results__stat">
          <span className="eval-results__stat-label">اكتمل تقييمهم</span>
          <strong>
            {stats.completed}/{stats.total}
          </strong>
        </div>
      </div>

      <section className="eval-results__missing" aria-label="من لم يرسلوا التقييم">
        <div className="eval-results__missing-head">
          <div>
            <h2 className="eval-results__round-title">من لم يرسلوا التقييم</h2>
            <p className="eval-results__round-meta">
              الموظفون المطلوب منهم إرسال تقييم خلال {missingMonthLabel} ولم يرسلوه
            </p>
          </div>
          <div className="eval-results__missing-actions">
            <div className="eval-results__field">
              <label htmlFor="eval-missing-month">الشهر</label>
              <input
                id="eval-missing-month"
                className="form-control"
                type="month"
                value={missingMonth}
                onChange={(e) => setMissingMonth(e.target.value)}
              />
            </div>
            <button
              type="button"
              className="btn btn-success"
              onClick={handleExportMissing}
              disabled={loading || missingSubmitters.length === 0}
            >
              تصدير التقرير
            </button>
          </div>
        </div>

        {loading ? null : missingSubmitters.length === 0 ? (
          <div className="eval-results__empty">
            {monthHasRounds
              ? "كل الموظفين المطلوب منهم التقييم أرسلوه خلال هذا الشهر."
              : "لا توجد جولات تقييم مفتوحة أو مغلقة في هذا الشهر."}
          </div>
        ) : (
          <div className="eval-results__list">
            <p className="eval-results__missing-count">
              {missingSubmitters.length} موظف لم يرسل التقييم
            </p>
            {missingSubmitters.map((row) => (
              <div className="eval-results__card" key={row.id}>
                <div className="eval-results__missing-row">
                  <div className="eval-results__identity">
                    <strong>{row.name}</strong>
                    <span className="text-muted">
                      {[row.code, row.jobTitle, row.branchName]
                        .filter(Boolean)
                        .join(" — ")}
                    </span>
                  </div>
                  <span className="eval-results__missing-badge">
                    لم يُرسل {row.pending.length}
                  </span>
                </div>
                <ul className="eval-results__raters">
                  {row.pending.map((item) => (
                    <li key={item.id}>
                      <span>
                        {item.roundTitle}: تقييم {item.subjectName}
                      </span>
                      <span>{item.raterTypeLabel}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </section>

      {loading ? (
        <p className="text-muted">جاري التحميل...</p>
      ) : sections.length === 0 ? (
        <div className="eval-results__empty">
          لا توجد تقييمات مفتوحة أو مغلقة مطابقة للفلتر.
        </div>
      ) : (
        <div className="eval-results__rounds">
          {sections.map((section) => (
            <section className="eval-results__round" key={section.round.id}>
              <div className="eval-results__round-head">
                <div>
                  <h2 className="eval-results__round-title">
                    {section.round.title}
                  </h2>
                  <p className="eval-results__round-meta">
                    <span
                      className={`eval-results__badge status-${section.round.status}`}
                    >
                      {statusLabel(section.round.status)}
                    </span>
                    <span>مسؤول الموارد البشرية: {creatorLabel(section.round)}</span>
                  </p>
                </div>
                {canDelete && section.round.status === "CLOSED" ? (
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    disabled={deletingId === section.round.id}
                    onClick={() => handleDelete(section.round)}
                  >
                    حذف التقييم المغلق
                  </button>
                ) : null}
              </div>

              {section.rows.length === 0 ? (
                <div className="eval-results__empty">
                  لا يوجد موظفون مطابقون للبحث في هذه الجولة.
                </div>
              ) : (
                <div className="eval-results__list">
                  {section.rows.map((row) => {
                    const open = expandedId === row.subject.id;
                    const tone = scoreTone(row.finalPercent);
                    return (
                      <div
                        className={`eval-results__card ${open ? "is-open" : ""}`}
                        key={row.subject.id}
                      >
                        <button
                          type="button"
                          className="eval-results__card-head"
                          onClick={() =>
                            setExpandedId(open ? null : row.subject.id)
                          }
                        >
                          <div className="eval-results__identity">
                            <strong>{row.name}</strong>
                            <span className="text-muted">
                              {[row.code, row.jobTitle, row.branchName]
                                .filter(Boolean)
                                .join(" — ")}
                            </span>
                            <span className="eval-results__progress">
                              تقدم المقيمين: {row.submittedCount}/
                              {row.totalRaters}
                            </span>
                          </div>
                          <div className={`eval-results__score tone-${tone}`}>
                            {row.finalPercent != null
                              ? `${row.finalPercent}%`
                              : "—"}
                          </div>
                        </button>

                        {open ? (
                          <div className="eval-results__card-body">
                            <ul className="eval-results__raters">
                              {(row.subject.raters || []).map((rater) => (
                                <li key={rater.id}>
                                  <span>
                                    {rater.rater?.fullName || "—"} (
                                    {raterTypeLabel(rater.raterType)})
                                  </span>
                                  <span>
                                    {rater.status === "SUBMITTED"
                                      ? `${Number(rater.scorePercent)}%`
                                      : "لم يُرسل"}
                                  </span>
                                </li>
                              ))}
                            </ul>
                            <EvaluationComparisonChart
                              perspective="hr"
                              questions={section.round.questions}
                              raters={row.subject.raters}
                            />
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
